import * as pdfjsLib from 'pdfjs-dist';

// Set worker source - use local version for reliability
pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/js/pdf.worker.min.js';

class PDFViewer {
    constructor() {
        this.currentPdf = null;
        this.currentPage = 1;
        this.totalPages = 0;
        this.scale = 1.0;
        this.canvas = null;
        this.context = null;
        
        this.initModal();
        this.bindEvents();
    }

    initModal() {
        // Create modal HTML if it doesn't exist
        if (!document.getElementById('pdf-viewer-modal')) {
            const modalHTML = `
                <div class="modal fade" id="pdf-viewer-modal" tabindex="-1" role="dialog" aria-labelledby="pdf-viewer-title" aria-hidden="true">
                    <div class="modal-dialog" style="max-width: 95vw; width: 95vw; height: 95vh; max-height: 95vh; margin: 2.5vh auto;" role="document">
                        <div class="modal-content" style="height: 100%; display: flex; flex-direction: column;">
                            <div class="modal-header" style="flex-shrink: 0;">
                                <h5 class="modal-title" id="pdf-viewer-title">PDF Viewer</h5>
                                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body" style="flex: 1; display: flex; flex-direction: column; padding: 1rem; overflow: hidden;">
                                <div class="pdf-viewer-controls mb-3 d-flex justify-content-between align-items-center flex-wrap" style="flex-shrink: 0;">
                                    <div class="pdf-navigation">
                                        <button id="pdf-prev-page" class="btn btn-outline-secondary btn-sm" disabled>
                                            <i class="fa-solid fa-chevron-left"></i> Previous
                                        </button>
                                        <span id="pdf-page-info" class="mx-3">Page 1 of 1</span>
                                        <button id="pdf-next-page" class="btn btn-outline-secondary btn-sm" disabled>
                                            Next <i class="fa-solid fa-chevron-right"></i>
                                        </button>
                                    </div>
                                    <div class="pdf-zoom-controls">
                                        <button id="pdf-zoom-out" class="btn btn-outline-secondary btn-sm">
                                            <i class="fa-solid fa-minus"></i>
                                        </button>
                                        <span id="pdf-zoom-level" class="mx-2">100%</span>
                                        <button id="pdf-zoom-in" class="btn btn-outline-secondary btn-sm">
                                            <i class="fa-solid fa-plus"></i>
                                        </button>
                                        <button id="pdf-fit-width" class="btn btn-outline-secondary btn-sm ms-2">
                                            Fit Width
                                        </button>
                                    </div>
                                </div>
                                <div class="pdf-viewer-container" style="flex: 1; text-align: center; overflow: auto; border: 1px solid #dee2e6; background-color: #f8f9fa; min-height: 0;">
                                    <div id="pdf-loading" class="text-center" style="padding: 50px;">
                                        <div class="spinner-border" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        <p class="mt-2">Loading PDF...</p>
                                    </div>
                                    <canvas id="pdf-canvas" style="display: none; margin: 10px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); border: 1px solid #ccc; max-width: none; max-height: none;"></canvas>
                                </div>
                            </div>
                            <div class="modal-footer" style="flex-shrink: 0;">
                                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            document.body.insertAdjacentHTML('beforeend', modalHTML);
        }

        this.canvas = document.getElementById('pdf-canvas');
        this.context = this.canvas.getContext('2d');
    }

    bindEvents() {
        // Navigation buttons
        document.getElementById('pdf-prev-page').addEventListener('click', () => {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.renderPage();
            }
        });

        document.getElementById('pdf-next-page').addEventListener('click', () => {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.renderPage();
            }
        });

        // Zoom buttons
        document.getElementById('pdf-zoom-in').addEventListener('click', () => {
            const oldScale = this.scale;
            // Increase by 50% (0.5) up to maximum of 300% (3.0)
            this.scale = Math.min(this.scale + 0.5, 3.0);
            console.log('Zoom in: scale changed from', oldScale, 'to', this.scale, '(' + Math.round(this.scale * 100) + '%)');
            this.renderPage();
        });

        document.getElementById('pdf-zoom-out').addEventListener('click', () => {
            const oldScale = this.scale;
            // Decrease by 50% (0.5) down to minimum of 50% (0.5)
            this.scale = Math.max(this.scale - 0.5, 0.5);
            console.log('Zoom out: scale changed from', oldScale, 'to', this.scale, '(' + Math.round(this.scale * 100) + '%)');
            this.renderPage();
        });

        document.getElementById('pdf-fit-width').addEventListener('click', () => {
            this.fitToWidth();
        });

        // PDF link click handler
        document.addEventListener('click', (e) => {
            const pdfLink = e.target.closest('[data-pdf-url]');
            if (pdfLink) {
                e.preventDefault();
                const url = pdfLink.getAttribute('data-pdf-url');
                const title = pdfLink.getAttribute('data-pdf-title') || 'PDF Document';
                this.openPDF(url, title);
            }
        });
    }

    async openPDF(url, title = 'PDF Document') {
        // Show modal
        const modal = new bootstrap.Modal(document.getElementById('pdf-viewer-modal'));
        modal.show();

        // Set title
        document.getElementById('pdf-viewer-title').textContent = title;

        // Show loading state
        document.getElementById('pdf-loading').style.display = 'block';
        this.canvas.style.display = 'none';

        try {
            console.log('Loading PDF from URL:', url);
            
            // First check if the URL is accessible
            const response = await fetch(url, { method: 'HEAD' });
            console.log('PDF URL response status:', response.status);
            console.log('PDF URL response headers:', [...response.headers.entries()]);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const contentType = response.headers.get('content-type');
            console.log('Content-Type:', contentType);
            
            if (contentType && !contentType.includes('application/pdf')) {
                console.warn('Content-Type is not PDF:', contentType);
            }

            // Load PDF
            this.currentPdf = await pdfjsLib.getDocument({
                url: url,
                withCredentials: true
            }).promise;
            
            this.totalPages = this.currentPdf.numPages;
            this.currentPage = 1;

            // Hide loading state
            document.getElementById('pdf-loading').style.display = 'none';
            this.canvas.style.display = 'block';

            // Render first page
            await this.renderPage();
            this.updateControls();

        } catch (error) {
            console.error('Error loading PDF:', error);
            console.error('PDF URL was:', url);
            document.getElementById('pdf-loading').innerHTML = `
                <div class="alert alert-danger">
                    <i class="fa-solid fa-exclamation-triangle"></i> 
                    <strong>Error loading PDF:</strong> ${error.message}
                    <br><small class="text-muted">URL: ${url}</small>
                    <br><small class="text-muted">Check browser console for more details</small>
                </div>
            `;
        }
    }

    async renderPage() {
        if (!this.currentPdf) return;

        const page = await this.currentPdf.getPage(this.currentPage);
        const viewport = page.getViewport({ scale: this.scale });

        // Get device pixel ratio for high DPI displays
        const devicePixelRatio = window.devicePixelRatio || 1;
        
        // Set the actual canvas size in memory (scaled up for high DPI)
        this.canvas.width = Math.floor(viewport.width * devicePixelRatio);
        this.canvas.height = Math.floor(viewport.height * devicePixelRatio);
        
        // Set the display size of the canvas (CSS pixels)
        this.canvas.style.width = Math.floor(viewport.width) + 'px';
        this.canvas.style.height = Math.floor(viewport.height) + 'px';
        
        // Reset any previous transforms and scale for high DPI
        this.context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

        // Clear the canvas before rendering
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Render page
        const renderContext = {
            canvasContext: this.context,
            viewport: viewport
        };

        try {
            await page.render(renderContext).promise;
            console.log('Page rendered at scale:', this.scale, 'dimensions:', Math.floor(viewport.width) + 'x' + Math.floor(viewport.height), 'DPR:', devicePixelRatio);
        } catch (error) {
            console.error('Error rendering page:', error);
        }
        
        this.updateControls();
    }

    updateControls() {
        // Update page info
        document.getElementById('pdf-page-info').textContent = `Page ${this.currentPage} of ${this.totalPages}`;

        // Update navigation buttons
        document.getElementById('pdf-prev-page').disabled = this.currentPage <= 1;
        document.getElementById('pdf-next-page').disabled = this.currentPage >= this.totalPages;

        // Update zoom level and button states
        document.getElementById('pdf-zoom-level').textContent = `${Math.round(this.scale * 100)}%`;
        document.getElementById('pdf-zoom-in').disabled = this.scale >= 3.0; // Disable at 300%
        document.getElementById('pdf-zoom-out').disabled = this.scale <= 0.5; // Disable at 50%
    }

    fitToWidth() {
        if (!this.currentPdf) return;

        const container = document.querySelector('.pdf-viewer-container');
        const containerWidth = container.clientWidth - 40; // Account for padding

        this.currentPdf.getPage(this.currentPage).then(page => {
            const viewport = page.getViewport({ scale: 1.0 });
            const calculatedScale = containerWidth / viewport.width;
            
            // Snap to nearest valid zoom level (50% increments)
            // Valid levels: 0.5, 1.0, 1.5, 2.0, 2.5, 3.0
            const validScales = [0.5, 1.0, 1.5, 2.0, 2.5, 3.0];
            
            // Find the closest valid scale
            let closestScale = validScales[0];
            let minDifference = Math.abs(calculatedScale - validScales[0]);
            
            for (let i = 1; i < validScales.length; i++) {
                const difference = Math.abs(calculatedScale - validScales[i]);
                if (difference < minDifference) {
                    minDifference = difference;
                    closestScale = validScales[i];
                }
            }
            
            console.log('Fit width: calculated scale:', calculatedScale, 'snapped to:', closestScale, '(' + Math.round(closestScale * 100) + '%)');
            
            this.scale = closestScale;
            this.renderPage();
        });
    }
}

// Initialize PDF viewer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.pdfViewer = new PDFViewer();
});

export default PDFViewer;
